import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import PropertyContext from "../contexts/PropertyContext";
import {
  getBuildingsForRooms,
  getFloorsForRooms,
  getRoomFloor,
  extractRoomNumber,
} from "../utils/floorInference";

// Mirror the tile sizing / status palette used by the map editor so the
// read-only view matches what was laid out there.
const DEFAULT_W = 130;
const DEFAULT_H = 90;

const STATUS_COLORS = {
  Up: "#1faa6c", // emerald
  Down: "#d11a45", // ruby
  Occupied: "#ffffff", // diamond
};
const statusColor = (status) => STATUS_COLORS[status] || "#9e9e9e";

// Glossy, faceted overlay + highlight that turns a flat fill into a gem.
const GEM_BACKGROUND_IMAGE =
  "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.12) 38%, rgba(0,0,0,0.16) 100%)";
const GEM_SHADOW =
  "0 2px 5px rgba(0,0,0,0.28), inset 0 1px 1px rgba(255,255,255,0.45)";
const GEM_SHADOW_HOVER =
  "0 4px 10px rgba(0,0,0,0.35), inset 0 1px 2px rgba(255,255,255,0.6)";

// Dark text on light tiles (e.g. Occupied = white), white text otherwise.
const LIGHT_TILES = new Set(["#ffffff", "#fff"]);
const textColorFor = (bg) => (LIGHT_TILES.has(bg.toLowerCase()) ? "#212121" : "#fff");

// Position of a room within its floor, shared across floors:
// 130 -> 30, 230 -> 30, 330 -> 30; "A-103" -> 3; "12" -> 12.
const floorUnitIndex = (displayName) => {
  const num = extractRoomNumber(displayName);
  if (num == null) return null;
  return num >= 100 ? num % 100 : num;
};

// Heatmap fill for a stacked unit: darker red the larger the share of Down
// rooms in the stack (all down = darkest, none down = neutral).
const heatStyle = (downCount, total) => {
  if (!total || downCount === 0) {
    return { bg: "#eeeeee", textColor: "#9e9e9e" };
  }
  const fraction = downCount / total; // 0..1
  const lightness = 80 - fraction * 50; // 80% (light) .. 30% (deep ruby)
  return {
    bg: `hsl(345, 80%, ${lightness}%)`, // ruby hue
    textColor: lightness < 60 ? "#fff" : "#212121",
  };
};

const ADJACENCY_STROKE = "#90a4ae";
const CONJOINED_STROKE = "#7b1fa2";

// Grid fallback for rooms that were never placed in the editor.
const defaultPosition = (index) => {
  const COLS = 6;
  return {
    x: (index % COLS) * (DEFAULT_W + 30),
    y: Math.floor(index / COLS) * (DEFAULT_H + 30),
  };
};

/**
 * Read-only room map for the rooms page Map View tab. Renders rooms at the
 * positions/sizes saved by the map editor (PropertyMap), colored by live
 * status. Does not edit or persist anything.
 */
const RoomsMapView = ({ onRoomClick }) => {
  const { rooms, propertyInformation } = useContext(PropertyContext);

  const propertyMap = propertyInformation?.propertyMap;
  const positions = propertyMap?.positions || {};
  const sizes = propertyMap?.sizes || {};
  const overrides = propertyMap?.floorOverrides || {};
  const savedEdges = propertyMap?.edges || [];

  const [building, setBuilding] = useState("");
  const [floor, setFloor] = useState("");
  const [activeStatus, setActiveStatus] = useState(null); // highlight a single status

  // Measure the container so we can scale the board to fit its width.
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const buildings = useMemo(() => getBuildingsForRooms(rooms), [rooms]);

  useEffect(() => {
    if (!building && buildings.length) setBuilding(buildings[0]);
  }, [buildings, building]);

  const buildingRooms = useMemo(
    () =>
      rooms.filter(
        (r) => String(r.Building != null ? r.Building : "1") === String(building)
      ),
    [rooms, building]
  );

  const floors = useMemo(
    () => getFloorsForRooms(buildingRooms, overrides),
    [buildingRooms, overrides]
  );

  useEffect(() => {
    if (!floors.length) return;
    if (floor === "heatmap") return;
    if (floor === "" || !floors.includes(Number(floor))) {
      setFloor(floors[0]);
    }
  }, [floors, floor]);

  const isHeatmap = floor === "heatmap";

  const viewRooms = useMemo(
    () =>
      buildingRooms.filter((r) => getRoomFloor(r, overrides) === Number(floor)),
    [buildingRooms, overrides, floor]
  );

  // Resolve a set of rooms into placed tiles (saved position/size, or a grid
  // fallback) normalized to a (0,0)-anchored board, plus the board's size.
  const computeBoard = useCallback(
    (roomList) => {
      const sorted = [...roomList].sort(
        (a, b) =>
          (extractRoomNumber(a.displayName) || 0) -
          (extractRoomNumber(b.displayName) || 0)
      );

      const placed = sorted.map((room, idx) => {
        const pos = positions[room.id] || defaultPosition(idx);
        const size = sizes[room.id] || { w: DEFAULT_W, h: DEFAULT_H };
        return { room, x: pos.x, y: pos.y, w: size.w, h: size.h };
      });

      if (!placed.length) {
        return { tiles: [], boardWidth: 0, boardHeight: 0 };
      }

      const minX = Math.min(...placed.map((t) => t.x));
      const minY = Math.min(...placed.map((t) => t.y));
      const maxX = Math.max(...placed.map((t) => t.x + t.w));
      const maxY = Math.max(...placed.map((t) => t.y + t.h));

      const normalized = placed.map((t) => ({
        ...t,
        x: t.x - minX,
        y: t.y - minY,
      }));

      const PAD = 20;
      return {
        tiles: normalized,
        boardWidth: maxX - minX + PAD * 2,
        boardHeight: maxY - minY + PAD * 2,
      };
    },
    [positions, sizes]
  );

  const { tiles, boardWidth, boardHeight } = useMemo(
    () => computeBoard(viewRooms),
    [computeBoard, viewRooms]
  );

  // Largest board across every building/floor. The biggest board is the one
  // that scales down the most, so using its scale everywhere gives a single
  // standard tile size for all maps (and guarantees nothing overflows).
  const { maxBoardWidth, maxBoardHeight } = useMemo(() => {
    const groups = {};
    rooms.forEach((r) => {
      const b = r.Building != null ? String(r.Building) : "1";
      const f = getRoomFloor(r, overrides);
      const key = `${b}|${f}`;
      (groups[key] = groups[key] || []).push(r);
    });

    let mw = 0;
    let mh = 0;
    Object.values(groups).forEach((list) => {
      const { boardWidth: bw, boardHeight: bh } = computeBoard(list);
      if (bw > mw) mw = bw;
      if (bh > mh) mh = bh;
    });
    return { maxBoardWidth: mw, maxBoardHeight: mh };
  }, [rooms, overrides, computeBoard]);

  // Connections between two rooms that are both on the current view.
  const edges = useMemo(() => {
    const byId = {};
    tiles.forEach((t) => {
      byId[t.room.id] = t;
    });
    const PAD = 20;
    return savedEdges
      .filter((e) => byId[e.source] && byId[e.target])
      .map((e) => {
        const a = byId[e.source];
        const b = byId[e.target];
        return {
          id: e.id,
          conjoined: e.type === "conjoined" || e.type === "suite",
          x1: a.x + a.w / 2 + PAD,
          y1: a.y + a.h / 2 + PAD,
          x2: b.x + b.w / 2 + PAD,
          y2: b.y + b.h / 2 + PAD,
        };
      });
  }, [savedEdges, tiles]);

  // Render-ready tiles for a single floor (status-colored, clickable).
  const normalTiles = useMemo(
    () =>
      tiles.map((t) => {
        const bg = statusColor(t.room.status);
        return {
          key: t.room.id,
          x: t.x,
          y: t.y,
          w: t.w,
          h: t.h,
          label: t.room.displayName,
          bg,
          textColor: textColorFor(bg),
          status: t.room.status,
          room: t.room,
        };
      }),
    [tiles]
  );

  // Heatmap: stack all floors of the building by unit-index and color each
  // stack by how many of its rooms are Down. Reuses one floor's layout for
  // placement (floors share positions via the editor's unit-index matching).
  const heatmapBoard = useMemo(() => {
    if (!isHeatmap) return { tiles: [], boardWidth: 0, boardHeight: 0 };

    // Group rooms by unit-index, and index each floor's placed-room positions
    // (floor -> { unitIndex -> {pos, size} }). Different floors can sit in
    // different coordinate frames (the editor normalizes copied floors), so we
    // anchor everything to one reference floor and translate the others.
    const groups = {};
    const byFloor = {};
    const floorCounts = {};
    buildingRooms.forEach((r) => {
      const ui = floorUnitIndex(r.displayName);
      if (ui == null) return;
      (groups[ui] = groups[ui] || []).push(r);
      const p = positions[r.id];
      if (p) {
        const f = getRoomFloor(r, overrides);
        byFloor[f] = byFloor[f] || {};
        byFloor[f][ui] = {
          pos: p,
          size: sizes[r.id] || { w: DEFAULT_W, h: DEFAULT_H },
        };
        floorCounts[f] = (floorCounts[f] || 0) + 1;
      }
    });

    // Reference frame = the floor with the most placed rooms (most complete).
    const refFloor = Object.keys(floorCounts).sort(
      (a, b) => floorCounts[b] - floorCounts[a]
    )[0];
    const refMap = (refFloor != null && byFloor[refFloor]) || {};

    // Translation mapping a floor's coords into the reference frame, estimated
    // from the units the floor shares with the reference floor.
    const offsetCache = {};
    const offsetFor = (f) => {
      if (f === refFloor) return { dx: 0, dy: 0 };
      if (offsetCache[f]) return offsetCache[f];
      const fMap = byFloor[f] || {};
      let sx = 0;
      let sy = 0;
      let n = 0;
      Object.keys(fMap).forEach((ui) => {
        if (refMap[ui]) {
          sx += refMap[ui].pos.x - fMap[ui].pos.x;
          sy += refMap[ui].pos.y - fMap[ui].pos.y;
          n += 1;
        }
      });
      const off = n ? { dx: sx / n, dy: sy / n } : { dx: 0, dy: 0 };
      offsetCache[f] = off;
      return off;
    };

    const entries = Object.entries(groups).sort(
      (a, b) => Number(a[0]) - Number(b[0])
    );

    const placed = entries.map(([ui, list], idx) => {
      let pos = null;
      let size = null;

      if (refMap[ui]) {
        // Present on the reference floor — use its canonical position.
        pos = refMap[ui].pos;
        size = refMap[ui].size;
      } else {
        // Otherwise take it from the most complete floor that has it, and
        // translate that floor's coords into the reference frame so the unit
        // lands next to the neighbors it sits beside on its own floor.
        const f = Object.keys(byFloor)
          .filter((fl) => byFloor[fl][ui])
          .sort((a, b) => floorCounts[b] - floorCounts[a])[0];
        if (f != null) {
          const { dx, dy } = offsetFor(f);
          pos = { x: byFloor[f][ui].pos.x + dx, y: byFloor[f][ui].pos.y + dy };
          size = byFloor[f][ui].size;
        }
      }

      if (!pos) pos = defaultPosition(idx);
      if (!size) size = { w: DEFAULT_W, h: DEFAULT_H };

      const downCount = list.filter((r) => r.status === "Down").length;
      const total = list.length;
      const { bg, textColor } = heatStyle(downCount, total);
      return {
        key: `ui-${ui}`,
        x: pos.x,
        y: pos.y,
        w: size.w,
        h: size.h,
        label: `${building}-X${String(ui).padStart(2, "0")}`,
        bg,
        textColor,
        title: `${downCount} of ${total} down`,
      };
    });

    if (!placed.length) return { tiles: [], boardWidth: 0, boardHeight: 0 };

    const minX = Math.min(...placed.map((t) => t.x));
    const minY = Math.min(...placed.map((t) => t.y));
    const maxX = Math.max(...placed.map((t) => t.x + t.w));
    const maxY = Math.max(...placed.map((t) => t.y + t.h));
    const PAD = 20;
    return {
      tiles: placed.map((t) => ({ ...t, x: t.x - minX, y: t.y - minY })),
      boardWidth: maxX - minX + PAD * 2,
      boardHeight: maxY - minY + PAD * 2,
    };
  }, [isHeatmap, buildingRooms, positions, sizes, overrides, building]);

  // Active board: heatmap stack or single floor.
  const board = isHeatmap
    ? heatmapBoard
    : { tiles: normalTiles, boardWidth, boardHeight };

  // One shared scale for every floor, derived from the LARGEST board so tiles
  // are a standard size across all maps. Fit that board into the available
  // width and a max viewport height, never larger than MAX_SCALE.
  const MAX_SCALE = 0.8;
  const MAX_VIEW_HEIGHT = 600;
  const fitWidth =
    maxBoardWidth && containerWidth
      ? (containerWidth - 20) / maxBoardWidth
      : MAX_SCALE;
  const fitHeight = maxBoardHeight ? MAX_VIEW_HEIGHT / maxBoardHeight : MAX_SCALE;
  const scale = Math.min(MAX_SCALE, fitWidth, fitHeight);

  if (!rooms.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        No rooms found for this property yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Building</InputLabel>
          <Select
            label="Building"
            value={buildings.includes(building) ? building : ""}
            onChange={(e) => setBuilding(e.target.value)}
          >
            {buildings.map((b) => (
              <MenuItem key={b} value={b}>
                Building {b}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Floor</InputLabel>
          <Select
            label="Floor"
            value={
              isHeatmap
                ? "heatmap"
                : floors.includes(Number(floor))
                ? Number(floor)
                : ""
            }
            onChange={(e) => setFloor(e.target.value)}
          >
            {floors.map((f) => (
              <MenuItem key={f} value={f}>
                Floor {f}
              </MenuItem>
            ))}
            <MenuItem value="heatmap">Heatmap (all floors)</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        {isHeatmap ? (
          // Heatmap gradient legend
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption">Fewer down</Typography>
            <Box
              sx={{
                width: 120,
                height: 12,
                borderRadius: 1,
                border: "1px solid rgba(0,0,0,0.2)",
                background: "linear-gradient(to right, #eeeeee, hsl(0,75%,30%))",
              }}
            />
            <Typography variant="caption">All down</Typography>
          </Stack>
        ) : (
          // Status legend — click to highlight only rooms of that status
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {Object.entries(STATUS_COLORS).map(([label, color]) => {
              const active = activeStatus === label;
              return (
                <Box
                  key={label}
                  onClick={() => setActiveStatus(active ? null : label)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    cursor: "pointer",
                    userSelect: "none",
                    border: "1px solid",
                    borderColor: active ? "primary.main" : "transparent",
                    bgcolor: active ? "action.selected" : "transparent",
                    opacity: activeStatus && !active ? 0.5 : 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: color,
                      border: "1px solid rgba(0,0,0,0.25)",
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: active ? 700 : 400 }}>
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      {!propertyMap && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          No saved map layout for this property yet — showing rooms in a default
          grid. Arrange them in Property Settings → Map.
        </Typography>
      )}

      {/* Full-width measurer: lets us size the board to the available width */}
      <Box ref={containerRef} sx={{ width: "100%" }}>
        {board.tiles.length === 0 ? (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.grey[50],
              p: 1,
            }}
          >
            <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No rooms on this floor.
            </Typography>
          </Box>
        ) : (
          // Bordered box hugs the board so there's no blank space to the right
          <Box
            sx={{
              display: "inline-block",
              maxWidth: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "auto",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.grey[900]
                  : theme.palette.grey[50],
              p: 1,
            }}
          >
            {/* Occupies the scaled (displayed) size so nothing extends past it */}
            <Box
              sx={{
                position: "relative",
                width: board.boardWidth * scale,
                height: board.boardHeight * scale,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: board.boardWidth,
                  height: board.boardHeight,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                {/* Connections behind the tiles (floor view only) */}
                {!isHeatmap && (
                  <svg
                    width={board.boardWidth}
                    height={board.boardHeight}
                    style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
                  >
                    {edges.map((e) => (
                      <line
                        key={e.id}
                        x1={e.x1}
                        y1={e.y1}
                        x2={e.x2}
                        y2={e.y2}
                        stroke={e.conjoined ? CONJOINED_STROKE : ADJACENCY_STROKE}
                        strokeWidth={e.conjoined ? 3 : 2}
                      />
                    ))}
                  </svg>
                )}

                {board.tiles.map((t) => {
                  const PAD = 20;
                  const dimmed =
                    !isHeatmap && activeStatus && t.status !== activeStatus;
                  const clickable = Boolean(t.room) && Boolean(onRoomClick);
                  // Scale the label to fill most of the tile, leaving padding.
                  const fontSize = Math.max(
                    11,
                    Math.min(t.h * 0.4, (t.w * 1.2) / Math.max((t.label || "").length, 1))
                  );
                  return (
                    <Box
                      key={t.key}
                      title={t.title}
                      onClick={clickable ? () => onRoomClick(t.room) : undefined}
                      sx={{
                        position: "absolute",
                        left: t.x + PAD,
                        top: t.y + PAD,
                        width: t.w,
                        height: t.h,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: t.bg,
                        backgroundImage: GEM_BACKGROUND_IMAGE,
                        border: "1px solid rgba(0,0,0,0.28)",
                        boxShadow: GEM_SHADOW,
                        opacity: dimmed ? 0.15 : 1,
                        filter: dimmed ? "grayscale(70%)" : "none",
                        cursor: clickable ? "pointer" : "default",
                        transition: "box-shadow 0.15s, transform 0.15s, opacity 0.15s",
                        "&:hover": clickable
                          ? { boxShadow: GEM_SHADOW_HOVER, transform: "translateY(-2px)" }
                          : undefined,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize,
                          lineHeight: 1,
                          color: t.textColor,
                          textShadow:
                            t.textColor === "#fff"
                              ? "0 1px 2px rgba(0,0,0,0.35)"
                              : "none",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RoomsMapView;
